import Dexie, { type EntityTable } from 'dexie';

export interface Session {
  id: string;
  childId: string;
  type: 'nursing' | 'pumping' | 'bottle';
  startTime: number;
  endTime: number | null;
  durationMs: number;
  side?: 'left' | 'right' | 'both';
  switchTimes?: number[];
  volumeMl?: number;
  volumePerSide?: { left: number; right: number };
  source?: 'fresh' | 'fridge' | 'freezer' | 'deep-freezer' | 'formula';
  storageEntryId?: string;
  satisfaction?: number;
  note?: string;
  deletedAt?: number;
  createdAt: number;
  updatedAt: number;
  syncedAt?: number;
}

export interface StorageEntry {
  id: string;
  childId: string;
  sessionId?: string;
  location: 'fridge' | 'freezer' | 'deep-freezer';
  originalVolumeMl: number;
  remainingVolumeMl: number;
  storedAt: number;
  expiresAt: number;
  lastUsedAt?: number;
  depletedAt?: number;
  label?: string;
  note?: string;
  deletedAt?: number;
  createdAt: number;
  updatedAt: number;
  syncedAt?: number;
}

export interface DiaperEntry {
  id: string;
  childId: string;
  type: 'wet' | 'dirty' | 'both';
  time: number;
  note?: string;
  deletedAt?: number;
  createdAt: number;
  updatedAt: number;
  syncedAt?: number;
}

export interface WeightEntry {
  id: string;
  childId: string;
  weightGrams: number;
  date: number;
  note?: string;
  deletedAt?: number;
  createdAt: number;
  updatedAt: number;
  syncedAt?: number;
}

export interface Reminder {
  id: string;
  childId: string;
  type: 'pump' | 'feed' | 'hydration' | 'meal' | 'custom';
  intervalMs: number;
  nextFireAt: number;
  enabled: boolean;
  label: string;
  createdAt: number;
  updatedAt: number;
}

export interface ChildProfile {
  id: string;
  name: string;
  dateOfBirth: number;
  gender?: 'male' | 'female' | 'other';
  lastBreastUsed: 'left' | 'right' | null;
  lastPumpVolume: number | null;
  lastBottleVolume: number | null;
  order: number;
  archived?: boolean;
  createdAt: number;
  updatedAt: number;
  syncedAt?: number;
}

export interface AppSettings {
  id: string;
  theme: 'light' | 'dark' | 'system';
  units: 'metric' | 'imperial';
  showHealthReferenceRanges: boolean;
  showGoals: boolean;
  feedAlertThresholdMs: number;
  syncEnabled: boolean;
  notificationsEnabled: boolean;
}

export interface AppState {
  id: 'singleton';
  activeChildId: string | null;
  onboardingCompleted: boolean;
}

export interface ActiveTimerState {
  id: string;
  childId: string;
  sessionType: 'nursing' | 'pumping';
  startTime: number;
  side?: 'left' | 'right' | 'both';
  switchTimes: number[];
}

const db = new Dexie('mamaobabyDB') as Dexie & {
  sessions: EntityTable<Session, 'id'>;
  storageEntries: EntityTable<StorageEntry, 'id'>;
  diapers: EntityTable<DiaperEntry, 'id'>;
  weights: EntityTable<WeightEntry, 'id'>;
  reminders: EntityTable<Reminder, 'id'>;
  children: EntityTable<ChildProfile, 'id'>;
  settings: EntityTable<AppSettings, 'id'>;
  appState: EntityTable<AppState, 'id'>;
  activeTimer: EntityTable<ActiveTimerState, 'id'>;
};

// Keep v1 declared so any developer with v1 data can upgrade without an open-error.
db.version(1).stores({
  sessions: 'id, type, startTime, deletedAt',
  storageEntries: 'id, location, storedAt, expiresAt, deletedAt',
  diapers: 'id, time, deletedAt',
  weights: 'id, date, deletedAt',
  reminders: 'id, type, nextFireAt',
  baby: 'id',
  settings: 'id',
  activeTimer: 'id',
});

db.version(2).stores({
  sessions:        'id, type, startTime, deletedAt, childId, [childId+startTime]',
  storageEntries:  'id, location, storedAt, expiresAt, deletedAt, childId, [childId+location]',
  diapers:         'id, time, deletedAt, childId, [childId+time]',
  weights:         'id, date, deletedAt, childId, [childId+date]',
  reminders:       'id, type, nextFireAt, childId',
  children:        'id, order, archived',
  baby:            null, // drop the old single-baby table
  settings:        'id',
  appState:        'id',
  activeTimer:     'id, childId',
}).upgrade(async (tx) => {
  // No production users; wipe all per-child data so we don't leave rows without childId.
  await tx.table('sessions').clear();
  await tx.table('storageEntries').clear();
  await tx.table('diapers').clear();
  await tx.table('weights').clear();
  await tx.table('reminders').clear();
  await tx.table('activeTimer').clear();
});

export { db };
